import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PlanRequest } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";

export default function EditPlan() {
  const navigate = useNavigate();
  const { id } = useParams();
  const planId = Number(id);
  const { setMessage } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    price: "",
    discount_price: "",
    usdt_price: "",
    usdt_discount_price: "",
    duration: "",
    is_active: true,
    is_featured: false,
  });

  const isFormValid = useMemo(() => {
    return Boolean(
      form.name.trim() &&
      form.price.trim() &&
      form.discount_price.trim() &&
      form.usdt_price.trim() &&
      form.usdt_discount_price.trim() &&
      form.duration.trim()
    );
  }, [form]);

  useEffect(() => {
    if (!planId) {
      setMessage({ type: "error", message: "شناسه پلن معتبر نیست." });
      navigate("/admin/plans");
      return;
    }

    const fetchPlan = async () => {
      setIsLoading(true);
      try {
        const response = await appRepo.getPlan(planId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت اطلاعات پلن ناموفق بود." });
          navigate("/admin/plans");
          return;
        }

        const data = response.data;
        setForm({
          name: data.name || "",
          price: String(data.price || ""),
          discount_price: String(data.discount_price || ""),
          usdt_price: String(data.usdt_price || ""),
          usdt_discount_price: String(data.usdt_discount_price || ""),
          duration: String(data.duration || ""),
          is_active: Boolean(data.is_active),
          is_featured: Boolean(data.is_featured),
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/plans");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlan();
  }, [navigate, planId, setMessage]);

  const onChangeField = (field: "name" | "price" | "discount_price" | "usdt_price" | "usdt_discount_price" | "duration", value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid || !planId) {
      setMessage({ type: "error", message: "لطفا همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: PlanRequest = {
        name: form.name.trim(),
        price: Number(form.price),
        discount_price: Number(form.discount_price),
        usdt_price: Number(form.usdt_price),
        usdt_discount_price: Number(form.usdt_discount_price),
        duration: Number(form.duration),
        is_active: form.is_active,
        is_featured: form.is_featured,
      };

      const response = await appRepo.updatePlan(planId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش پلن با خطا مواجه شد." });
        return;
      }

      setMessage({ type: "success", message: "پلن با موفقیت ویرایش شد." });
      navigate("/admin/plans");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. لطفا دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش پلن" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت اطلاعات پلن...
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش پلن" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm text-slate-300">نام پلن</span>
            <input
              value={form.name}
              onChange={(event) => onChangeField("name", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">قیمت</span>
            <input
              type="number"
              min="0"
              value={form.price}
              onChange={(event) => onChangeField("price", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">قیمت با تخفیف</span>
            <input
              type="number"
              min="0"
              value={form.discount_price}
              onChange={(event) => onChangeField("discount_price", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">قیمت ارزی</span>
            <input
              type="text"
              min="0"
              value={form.usdt_price}
              onChange={(event) => onChangeField("usdt_price", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">قیمت ارزی با تخفیف</span>
            <input
              type="text"
              min="0"
              value={form.usdt_discount_price}
              onChange={(event) => onChangeField("usdt_discount_price", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">مدت (روز)</span>
            <input
              type="number"
              min="1"
              value={form.duration}
              onChange={(event) => onChangeField("duration", event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
              className="h-4 w-4 accent-indigo-500"
            />
            <span className="text-sm text-slate-300">پلن فعال باشد</span>
          </label>

          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.is_featured}
              onChange={(event) => setForm((prev) => ({ ...prev, is_featured: event.target.checked }))}
              className="h-4 w-4 accent-indigo-500"
            />
            <span className="text-sm text-slate-300">پلن ویژه باشد</span>
          </label>

          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/plans"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
